/**
 * Rebuilt per assets/handoffs/design_handoff_integrations_page — one
 * Connections card (status-precedence rows) replacing the old three
 * h2-grouped sections of freestanding IntegrationCards. Connect happens on
 * real routes (/integrations/connect/:provider[/calendars]), not a modal —
 * OAuth does a full-page nav (window.open(authUrl, "_self")) that a modal
 * can't survive. Editing an existing connection opens IntegrationSettingsDialog
 * in place.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import PageTemplate from "./../templates/PageTemplate";
import Button from "../theme/untitled/Button/Button";
import Icon from "../theme/untitled/icons/Icon";
import ConnectionsCard from "./IntegrationsPage/ConnectionsCard";
import AddIntegrationSheet from "./IntegrationsPage/AddIntegrationSheet";
import DisconnectDialog from "./IntegrationsPage/DisconnectDialog";
import FullResyncDialog from "./IntegrationsPage/FullResyncDialog";
import IntegrationSettingsDialog from "./IntegrationsPage/IntegrationSettingsDialog/IntegrationSettingsDialog";
import { getQuotaSummary } from "./IntegrationsPage/getQuotaSummary";
import { useIntegrationsStore } from "../../../hooks/redux/integrations";
import { useIntegrationStore } from "../../../hooks/redux/integration";
import { useCurrentOrganizationStore } from "../../../hooks/redux/currentOrganization";
import { useUserStore } from "../../../hooks/redux/user";
import { openAuthWindow } from "../consts/pco";
import useQuery from "../../../hooks/useQuery";
import appConfig from "../../../appConfig";

const IntegrationsPage = () => {
  const history = useHistory();
  const query = useQuery();
  const { currentOrganization } = useCurrentOrganizationStore();
  const { checkSession } = useUserStore();
  const { integrations, fetchIntegrations } = useIntegrationsStore();
  const { updateIntegration, syncIntegration, deleteIntegration } =
    useIntegrationStore();

  const [addSheetOpen, setAddSheetOpen] = useState(false);
  const [pendingDisconnect, setPendingDisconnect] = useState(null);
  const [pendingFullResync, setPendingFullResync] = useState(null);
  const [pendingSettings, setPendingSettings] = useState(null);
  const [pendingSettingsTab, setPendingSettingsTab] = useState("calendars");

  useEffect(() => {
    fetchIntegrations();
  }, []);

  // Deep-link contract for callers with no route of their own (dashboard
  // widget's Reconnect, sidebar sync-issue alerts, the scope-issue
  // notification): ?settings=<uuid>[&reason=permissions] opens this dialog
  // (Sync tab by default, Scopes tab when the reason is permissions);
  // ?reauth=<uuid> skips the dialog and goes straight to the provider.
  // ?autosync=<uuid> is IntegrationAccessCheckPage.js's own redirect after
  // a successful OAuth round-trip for a connection that already has synced
  // calendars (a scope re-consent or a plain reconnect, as opposed to first
  // connect, which lands on the calendar picker instead) — regaining access
  // doesn't pull anything in by itself, so without this a re-granted scope
  // (e.g. Events Attachments) sits unused until a manual Resync.
  // All three need the collection loaded first to resolve the uuid to a row.
  useEffect(() => {
    if (!integrations.fetchSuccess) {
      return;
    }

    const settingsUuid = query.get("settings");
    const reauthUuid = query.get("reauth");
    const autosyncUuid = query.get("autosync");

    if (settingsUuid) {
      const found = integrations.collection.find(
        (i) => i.uuid === settingsUuid
      );
      if (found) {
        setPendingSettingsTab(
          query.get("reason") === "permissions" ? "scopes" : "sync"
        );
        setPendingSettings(found);
      }
      history.replace("/integrations");
      return;
    }

    if (reauthUuid) {
      const found = integrations.collection.find((i) => i.uuid === reauthUuid);
      if (found?.auth_url) {
        openAuthWindow(found.auth_url);
      }
      history.replace("/integrations");
      return;
    }

    if (autosyncUuid) {
      const found = integrations.collection.find((i) => i.uuid === autosyncUuid);
      if (found) {
        syncIntegration(found.uuid, false);
      }
      history.replace("/integrations");
    }
  }, [integrations.fetchSuccess]);

  const quota = useMemo(
    () => getQuotaSummary(currentOrganization),
    [currentOrganization]
  );
  const isLoading = integrations.fetch && !integrations.fetchSuccess;
  const pageError = integrations.deleteError || integrations.syncError;
  const addDisabled = Boolean(quota?.blockedByPlan);
  const disabledCount = integrations.collection.filter(
    (i) => i.disabled_date
  ).length;

  const goToConnect = useCallback(
    (provider) => {
      setAddSheetOpen(false);
      history.push(`/integrations/connect/${provider}`);
    },
    [history]
  );

  const onToggleAutoSync = useCallback(
    (integration) => {
      updateIntegration(integration.uuid, {
        available: integration.available,
        is_auto_sync: Number(integration.is_auto_sync) ? 0 : 1,
        name: integration.name,
        pull_events_ahead: integration.pull_events_ahead,
        integration_scopes: (integration.integration_scopes || []).map(
          (scope) => ({
            uuid: scope.uuid,
            name: scope.name,
            available: scope.available,
          })
        ),
      });
    },
    [updateIntegration]
  );

  const onResync = useCallback(
    (integration) => syncIntegration(integration.uuid, false),
    [syncIntegration]
  );

  const onFullResyncConfirm = useCallback(async () => {
    await syncIntegration(pendingFullResync.uuid, true);
    setPendingFullResync(null);
  }, [syncIntegration, pendingFullResync]);

  const onDisconnectConfirm = useCallback(async () => {
    await deleteIntegration(pendingDisconnect.uuid);
    setPendingDisconnect(null);
    // The row itself is gone now — if Disconnect was triggered from inside
    // the Settings dialog (Sync tab), that dialog would otherwise keep
    // showing a now-deleted connection underneath the confirm dialog.
    setPendingSettings((current) =>
      current?.uuid === pendingDisconnect.uuid ? null : current
    );
    // deleteIntegration only patches the integrations collection — nothing
    // refreshes currentOrganization.model.usage.integrations, so the quota
    // footer/"Add integration" gate would stay stale (still "at limit")
    // until the next full page load. checkSession() is the same call that
    // populates usage in the first place.
    checkSession();
  }, [deleteIntegration, pendingDisconnect, checkSession]);

  const onEditSettings = useCallback((integration) => {
    setPendingSettingsTab("calendars");
    setPendingSettings(integration);
  }, []);

  const onOpenAddSheet = useCallback(() => setAddSheetOpen(true), []);
  const onCloseAddSheet = useCallback(() => setAddSheetOpen(false), []);
  const onCloseDisconnect = useCallback(() => setPendingDisconnect(null), []);
  const onCloseFullResync = useCallback(() => setPendingFullResync(null), []);
  const onCloseSettings = useCallback(() => setPendingSettings(null), []);

  const rowProps = useMemo(
    () => ({
      onToggleAutoSync,
      onResync,
      onEditSettings,
      onFullResync: setPendingFullResync,
      onDisconnect: setPendingDisconnect,
    }),
    [onToggleAutoSync, onResync, onEditSettings]
  );

  // Frame 10's disabled "Add integration" carries no adjacent reason text —
  // the "why" lives in the footer band only (prototype/integrations-frames.js
  // set(9,...)); a plain disabled button is correct here.
  const addButton = useMemo(
    () => (
      <Button
        key="add-integration"
        iconLeading={<Icon name="Plus" />}
        onClick={onOpenAddSheet}
        disabled={addDisabled}
      >
        Add integration
      </Button>
    ),
    [onOpenAddSheet, addDisabled]
  );

  const headerControls = useMemo(() => [addButton], [addButton]);

  return (
    <PageTemplate
      sidebarId="integrations"
      title="Integrations"
      subtitle={`Connect a data source and ${appConfig.displayName} keeps its data in sync. Changes in the source appear here as they happen.`}
      headerControls={headerControls}
    >
      <ConnectionsCard
        integrations={integrations.collection}
        isLoading={isLoading}
        pageError={pageError}
        quota={quota}
        disabledCount={disabledCount}
        savingUuid={integrations.update ? integrations.updateUUID : null}
        onSelectProvider={goToConnect}
        rowProps={rowProps}
      />

      <AddIntegrationSheet
        open={addSheetOpen}
        onClose={onCloseAddSheet}
        onSelectProvider={goToConnect}
      />

      <DisconnectDialog
        integration={pendingDisconnect}
        busy={integrations.delete}
        onClose={onCloseDisconnect}
        onConfirm={onDisconnectConfirm}
      />

      <FullResyncDialog
        integration={pendingFullResync}
        busy={integrations.sync}
        onClose={onCloseFullResync}
        onConfirm={onFullResyncConfirm}
      />

      <IntegrationSettingsDialog
        integration={pendingSettings}
        initialTab={pendingSettingsTab}
        onClose={onCloseSettings}
        onFullResync={setPendingFullResync}
        onDisconnect={setPendingDisconnect}
      />
    </PageTemplate>
  );
};

export default IntegrationsPage;
